import { RefreshCw } from "lucide-react";
import { btnPrimaryClass, inputBaseClass } from "./constants";
import { fmtNotificationDate } from "./formatDate";

export function EventLogsTab({
  logFilter,
  setLogFilter,
  logLimit,
  setLogLimit,
  logs,
  onLoadLogs
}) {
  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">
              Filter by event type
            </label>
            <input
              className={`${inputBaseClass} w-[220px]`}
              value={logFilter}
              onChange={(e) => setLogFilter(e.target.value)}
              placeholder="extraction.completed"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">Limit</label>
            <select
              className={inputBaseClass}
              value={logLimit}
              onChange={(e) => setLogLimit(e.target.value)}
            >
              <option>20</option>
              <option>50</option>
              <option>100</option>
            </select>
          </div>
          <button
            type="button"
            onClick={onLoadLogs}
            className={`${btnPrimaryClass} px-3 py-2 text-xs`}
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Load Logs
          </button>
        </div>
      </div>

      {logs === null ? (
        <div className="text-center py-12 text-gray-400 text-sm">
          Click &quot;Load Logs&quot; to fetch recent events.
        </div>
      ) : !logs.length ? (
        <div className="text-center py-12 text-gray-400 text-sm">
          No events found.
        </div>
      ) : (
        logs.map((log, i) => {
          const dispatched = Array.isArray(log.dispatched_to) ? log.dispatched_to : [];
          const allOk =
            dispatched.length > 0 && dispatched.every((d) => d.success);
          const failCount = dispatched.filter((d) => !d.success).length;
          // A suppressed entry succeeded *by not sending* — it was a duplicate of
          // something already delivered. Counting it as a plain dispatch would
          // hide the deduplication; counting it as a failure would be wrong.
          const suppressedCount = dispatched.filter((d) => d.suppressed).length;
          const sentCount = dispatched.length - suppressedCount;

          return (
            <div
              key={log.id ?? i}
              className="bg-white border border-gray-200 rounded-xl p-4 mb-3"
            >
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700">
                    {log.event_type || "unknown"}
                  </span>
                  {dispatched.length > 0 &&
                    (allOk ? (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-50 text-green-700">
                        ✓ {sentCount} dispatched
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-700">
                        ⚠ {failCount} failed
                      </span>
                    ))}
                  {suppressedCount > 0 && (
                    <span
                      className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-800"
                      title="Duplicate of an event already delivered, so nothing was re-sent"
                    >
                      ⏸ {suppressedCount} duplicate
                    </span>
                  )}
                  {dispatched.length === 0 && (
                    <span
                      className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600"
                      title="Received, but no enabled subscription matched this event — nothing was sent anywhere"
                    >
                      no subscribers
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  {fmtNotificationDate(log.received_at)}
                </span>
              </div>
              <pre className="bg-[#1e1e2e] text-[#cdd6f4] rounded-lg p-3 text-xs font-mono max-h-[200px] overflow-auto whitespace-pre-wrap break-words">
                {JSON.stringify(log.payload, null, 2)}
              </pre>
              {dispatched.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs font-medium text-gray-600 mb-1">
                    Dispatch results:
                  </div>
                  {dispatched.map((d, j) => {
                    // Endpoint dispatches carry no webhook_name — fall back to the
                    // endpoint identity, and never render an empty line.
                    const viaEndpoint = d.destination === "endpoint" || !!d.endpoint_id;
                    const label =
                      d.webhook_name ||
                      (viaEndpoint ? "Connected API" : null) ||
                      d.webhook_url ||
                      "destination";
                    const receipt =
                      d.response_body &&
                      typeof d.response_body === "object" &&
                      (d.response_body.eventId || d.response_body.event_id);

                    return (
                      <div
                        key={j}
                        className={`text-xs mb-1 ${
                          d.suppressed
                            ? "text-amber-800"
                            : d.success
                            ? "text-green-700"
                            : "text-red-600"
                        }`}
                      >
                        {d.suppressed ? "⏸" : d.success ? "✓" : "✗"}{" "}
                        <strong>{label}</strong>
                        {d.status_code ? (
                          <span className="text-gray-500"> · HTTP {d.status_code}</span>
                        ) : null}
                        {d.suppressed ? (
                          <span className="text-gray-500">
                            {" "}
                            · duplicate, not re-sent
                          </span>
                        ) : null}
                        {receipt ? (
                          <span className="text-gray-500 font-mono"> · {receipt}</span>
                        ) : null}
                        {d.error ? ` — ${d.error}` : ""}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      )}
    </>
  );
}
